'use client';

import { message } from 'antd';
import { useCallback, useState } from 'react';

import { useTranslation } from '@/i18n/client';
import { EOperationType, type IParticipantUserInfo, circleMemberManage } from '@/service/friend';

import GroupMemberSelect from '../../GroupMemberSelect';

export interface IInviteMemberModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  onSuccess?: () => void;
}

export const InviteMemberModal = (props: IInviteMemberModalProps): JSX.Element => {
  const { open, setOpen, circleRoute, onSuccess } = props;
  const { t } = useTranslation();

  const [selectedMembers, setSelectedMembers] = useState<IParticipantUserInfo[]>([]);
  const [inviteLoading, setInviteLoading] = useState(false);

  // 选择/取消选择成员
  const handleSelectMember = useCallback((member: IParticipantUserInfo) => {
    setSelectedMembers((prev) => {
      const isSelected = prev.some((m) => m.id === member.id);

      if (isSelected) {
        return prev.filter((m) => m.id !== member.id);
      }

      return [...prev, member];
    });
  }, []);

  // 关闭弹窗
  const handleClose = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);

      if (!isOpen) {
        setSelectedMembers([]);
      }
    },
    [setOpen]
  );

  // 邀请成员加入群聊
  const handleInviteMembers = useCallback(() => {
    if (selectedMembers.length === 0) {
      message.warning(t('friend.selectMemberTip'));

      return;
    }

    setInviteLoading(true);

    const memberIds = selectedMembers.map((m) => m.id);

    circleMemberManage({
      route: circleRoute,
      operationType: EOperationType.ADD,
      userIds: memberIds
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.inviteSuccess'));
        handleClose(false);
        // 刷新群成员列表
        onSuccess?.();
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setInviteLoading(false);
      });
  }, [circleRoute, handleClose, onSuccess, selectedMembers, t]);

  return (
    <GroupMemberSelect
      checkTypes={['not_in_circle']}
      circleRoute={circleRoute}
      className="h-[calc(100vh-64px-32px)]"
      confirmText={t('circle.invite')}
      createLoading={inviteLoading}
      handleSelect={handleSelectMember}
      loadingText={t('common.inviting')}
      onCreate={handleInviteMembers}
      open={open}
      selectedIds={selectedMembers.map((m) => m.id)}
      setOpen={handleClose}
      title={t('circle.inviteFriend')}
    />
  );
};

export default InviteMemberModal;
